import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useEffect, useRef, useState } from "react";
import { css } from "styled-components";

/**
 * Detects whether the current connection is too slow for video streaming.
 * Uses the Network Information API (navigator.connection) when available.
 * Returns true on 2G/slow-2G or when downlink is below 1.5 Mbps.
 * Falls back to false (allow video) when the API is not supported.
 */
export function isSlowConnection(): boolean {
    var conn = (navigator as any).connection || (navigator as any).mozConnection || (navigator as any).webkitConnection;
    if (!conn) {
        return false;
    }
    var dominated = ["slow-2g", "2g", "3g"];
    if (conn.effectiveType && dominated.indexOf(conn.effectiveType) !== -1) {
        return true;
    }
    if (typeof conn.downlink === "number" && conn.downlink < 1.5) {
        return true;
    }
    return false;
}

export interface VideoBannerProps {
    src: string;
    poster?: string;
    fallbackImage?: string;
    loop: boolean;
    muted: boolean;
    autoplay: boolean;
    hideControls: boolean;
    playsInline: boolean;
    aspectRatio: string;
    verticalTextAlignment?: string;
    children?: React.ReactNode;
    onClick?: () => void;
}

const videoOverlayContent: InjectableCss = {
    css: css`
        position: relative;
        z-index: 1;
        width: 100%;
        height: 100%;
        display: flex;
    `,
};

const VideoBanner: FC<VideoBannerProps> = ({
    src,
    poster,
    fallbackImage,
    loop,
    muted,
    autoplay,
    hideControls,
    playsInline,
    aspectRatio,
    verticalTextAlignment,
    children,
    onClick,
}) => {
    var videoRef = useRef<HTMLVideoElement | null>(null);
    var containerRef = useRef<HTMLDivElement | null>(null);
    var [isVisible, setIsVisible] = useState(false);
    var [useFallback, setUseFallback] = useState(false);

    // Detect slow connection on mount and fall back to static image
    useEffect(function () {
        if (isSlowConnection()) {
            setUseFallback(true);
        }
    }, []);

    // Intersection Observer: only start playback when the banner scrolls into view
    useEffect(function () {
        var node = containerRef.current;
        if (!node || useFallback) {
            return;
        }
        var observer = new IntersectionObserver(
            function (entries) {
                setIsVisible(entries[0].isIntersecting);
            },
            { threshold: 0.25 },
        );
        observer.observe(node);
        return function () {
            observer.disconnect();
        };
    }, [useFallback]);

    // Play/pause based on viewport visibility
    useEffect(function () {
        var node = videoRef.current;
        if (!node || !autoplay || useFallback) {
            return;
        }
        if (isVisible) {
            var playPromise = node.play();
            if (playPromise && typeof playPromise.catch === "function") {
                playPromise.catch(function () {
                    /* autoplay blocked - nothing to do without user interaction */
                });
            }
        } else {
            node.pause();
        }
    }, [isVisible, autoplay, useFallback]);

    var containerStyles: InjectableCss = {
        css: css`
            position: relative;
            width: 100%;
            aspect-ratio: ${aspectRatio};
            overflow: hidden;
            display: flex;
            justify-content: ${verticalTextAlignment || "center"};
            align-items: center;
            flex-direction: column;
            color: white;
            text-align: center;
        `,
    };

    var fallbackSrc = fallbackImage || poster;

    // Slow connection: show static image instead of video
    if (useFallback && fallbackSrc) {
        return (
            <StyledWrapper {...containerStyles} onClick={onClick}>
                <img
                    src={fallbackSrc}
                    alt=""
                    style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        zIndex: 0,
                    }}
                />
                {children && <StyledWrapper {...videoOverlayContent}>{children}</StyledWrapper>}
            </StyledWrapper>
        );
    }

    return (
        <StyledWrapper {...containerStyles} onClick={onClick} ref={containerRef}>
            <video
                ref={videoRef}
                src={isVisible ? src : undefined}
                poster={poster || undefined}
                loop={loop}
                muted={muted}
                controls={!hideControls}
                playsInline={playsInline}
                disablePictureInPicture
                disableRemotePlayback
                preload="metadata"
                {...(hideControls
                    ? { controlsList: "nodownload nofullscreen noremoteplayback noplaybackrate" }
                    : {})}
                style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    zIndex: 0,
                    pointerEvents: "none",
                }}
            />
            {children && <StyledWrapper {...videoOverlayContent}>{children}</StyledWrapper>}
        </StyledWrapper>
    );
};

export default VideoBanner;
